import React, { useRef, useState } from "react";
import { Loader2, Play, Save, Palette } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SETTINGS, FX, DEF_FX, Knob, useTake, TakePlayer, errMsg } from "@/components/voiceKit";

const DEF_SET = { stability: 0.75, similarity_boost: 0.9, style: 0.15, speed: 1.1 };

const merge = (voice, st) => {
  const settings = { ...DEF_SET, ...voice.settings, ...st.settings };
  const fx = { ...DEF_FX, ...voice.fx };
  Object.entries(st.fx || {}).forEach(([k, x]) => { if (x !== DEF_FX[k]) fx[k] = x; });
  return { settings, fx };
};

const StyleForm = ({ style, voices, sampleText, onSaved }) => {
  const [s, setS] = useState({ ...style, settings: { ...style.settings }, fx: { ...style.fx } });
  const [vid, setVid] = useState(voices[0]?.id);
  const take = useTake();
  const timer = useRef(null);
  const voice = voices.find((x) => x.id === vid) || voices[0];
  const gen = (st = s) => { const m = merge(voice, st); take.generate({ preset: voice.preset, source: voice.source || "preset", voice_id: voice.id, text: sampleText, gender: voice.gender || "", settings: m.settings }, m.fx); };
  const setFx = (k, x) => {
    const n = { ...s, fx: { ...s.fx, [k]: x } }; setS(n);
    clearTimeout(timer.current); timer.current = setTimeout(() => take.applyFx(merge(voice, n).fx), 350);
  };
  const save = async () => {
    try { await api.put(`/admin/voice-styles/${s.id}`, { name: s.name, settings: s.settings, fx: s.fx }); toast.success(`تم اعتماد أسلوب ${s.name}`); onSaved(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const val = (grp, k, d) => (s[grp][k] ?? d);
  return (
    <div className="space-y-4" data-testid={`style-form-${s.id}`}>
      <div className="grid sm:grid-cols-2 gap-3">
        <Input value={s.name} onChange={(e) => setS({ ...s, name: e.target.value })} className="bg-secondary border-border" data-testid="style-name-input" />
        <Select value={vid} onValueChange={setVid}>
          <SelectTrigger className="bg-secondary border-border" data-testid="style-voice-select"><SelectValue /></SelectTrigger>
          <SelectContent>{voices.map((x) => <SelectItem key={x.id} value={x.id}>جرّب بصوت {x.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="grid sm:grid-cols-2 gap-6">
        <div className="space-y-4">
          {SETTINGS.filter(([k]) => k !== "similarity_boost").map(([k, l, a, b, st, h]) => (
            <Knob key={k} k={`style-${k}`} label={l} min={a} max={b} step={st} help={h} badge="توليد" value={val("settings", k, voice?.settings?.[k] ?? DEF_SET[k])}
              onChange={(x) => setS({ ...s, settings: { ...s.settings, [k]: x } })} onCommit={(x) => take.rawId && gen({ ...s, settings: { ...s.settings, [k]: x } })} />
          ))}
        </div>
        <div className="space-y-4">
          {FX.map(([k, l, a, b, st, h]) => <Knob key={k} k={`style-${k}`} label={l} min={a} max={b} step={st} help={h} value={val("fx", k, DEF_FX[k])} onChange={(x) => setFx(k, x)} />)}
          <label className="flex items-center justify-between text-xs font-medium">صوت إذاعي<Switch checked={!!s.fx.radio} onCheckedChange={(c) => setFx("radio", c)} data-testid="style-fx-radio" /></label>
        </div>
      </div>
      <div className="flex gap-2 flex-wrap">
        <Button variant="outline" onClick={() => gen()} disabled={take.busy || !voice} className="gap-2 border-border" data-testid="style-preview-btn">
          {take.busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} {take.rawId ? "توليد من جديد" : "توليد وتجربة"}
        </Button>
        <Button onClick={save} className="gap-2" data-testid="style-save-btn"><Save className="w-4 h-4" /> حفظ واعتماد الأسلوب</Button>
      </div>
      <TakePlayer take={take} testid="style-preview-audio" />
    </div>
  );
};

export const StyleEditor = ({ styles, voices, sampleText, onSaved }) => {
  const [cur, setCur] = useState(styles[0]?.id);
  const style = styles.find((x) => x.id === cur);
  return (
    <div className="border border-border rounded-xl p-4 space-y-3" data-testid="style-editor">
      <div className="font-bold text-sm flex items-center gap-2"><Palette className="w-4 h-4 text-primary" /> أساليب الإلقاء للعملاء</div>
      <p className="text-xs text-muted-foreground">العميل يختار واحد من هذه الأساليب عند توليد الفيديو، وتنطبق إعداداتها فوق إعدادات الشخصية. اختر أسلوب، جرّبه بصوت أي شخصية، حرّك المؤثرات واسمع الفرق فوراً، ثم اعتمد.</p>
      <div className="flex gap-2 flex-wrap">
        {styles.map((x) => (
          <button key={x.id} type="button" onClick={() => setCur(x.id)} data-testid={`style-tab-${x.id}`}
            className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${cur === x.id ? "bg-primary text-white border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>{x.name}</button>
        ))}
      </div>
      {style && voices.length > 0 && <StyleForm key={style.id} style={style} voices={voices} sampleText={sampleText} onSaved={onSaved} />}
    </div>
  );
};
